import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import BtnTaratic from './BtnTaratic';
import { useResolvedTheme, type ResolvedTheme } from './useResolvedTheme';

interface EditorCardProps {
  title: ReactNode;
  icon?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  onClose?: () => void;
  closeLabel?: string;
  variant?: ResolvedTheme;
  maxWidth?: '2xl' | '4xl' | 'full';
  className?: string;
  dataUi?: string;
  headerDataUi?: string;
  bodyDataUi?: string;
  footerDataUi?: string;
  dataTour?: string;
}

const maxWidthClasses = {
  '2xl': 'max-w-2xl',
  '4xl': 'max-w-4xl',
  full: 'max-w-none',
};

export default function EditorCard({
  title,
  icon,
  children,
  footer,
  onClose,
  closeLabel = 'Close',
  variant,
  maxWidth = '2xl',
  className = '',
  dataUi = 'editor-card',
  headerDataUi,
  bodyDataUi,
  footerDataUi,
  dataTour,
}: EditorCardProps) {
  const theme = useResolvedTheme(variant);

  return (
    <section
      data-ui={dataUi}
      data-editor-card="true"
      data-theme={theme}
      data-tour={dataTour}
      className={`editor-card relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl border border-white/20 bg-[#0f1115] shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md ${maxWidthClasses[maxWidth]} ${className}`}
    >
      <header
        data-ui={headerDataUi}
        className="editor-card-header flex items-center justify-between border-b border-white/20 bg-black/20 px-6 py-4"
      >
        <h2 className="flex items-center gap-2 text-xl font-bold text-white">
          {icon}
          {title}
        </h2>
        {onClose && (
          <BtnTaratic
            type="button"
            onClick={onClose}
            color="ghost-dark"
            iconOnly
            data-ui="editor-card-close"
            className="!h-8 !w-8"
            aria-label={closeLabel}
          >
            <X aria-hidden="true" size={18} strokeWidth={2.5} />
          </BtnTaratic>
        )}
      </header>

      <div
        data-ui={bodyDataUi}
        className="editor-card-body custom-scrollbar flex-1 overflow-y-auto bg-transparent p-6"
      >
        {children}
      </div>

      {footer && (
        <footer
          data-ui={footerDataUi}
          className="editor-card-footer flex justify-end gap-3 border-t border-white/20 bg-black/20 px-6 py-4"
        >
          {footer}
        </footer>
      )}
    </section>
  );
}
